<template>
<div :style="themePageStyle">
  <div class="container" style="padding-top:24px;padding-bottom:100px;max-width:900px;">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <div class="d-flex align-items-center">
        <button class="btn btn-sm btn-outline-secondary me-2" @click="$router.go(-1)"><i class="mdi mdi-arrow-left me-1"></i>Back</button>
        <h5 class="mb-0 fw-bold" :style="{color:darkMode?'#F5F0E8':'#1A1A1A'}"><i class="mdi mdi-file-document-multiple me-2" style="color:#BFA164;"></i>My Bills</h5>
      </div>
      <button class="btn btn-warning btn-sm fw-bold" @click="$router.push('/sell/bills/new')">
        <i class="mdi mdi-plus me-1"></i>New Bill
      </button>
    </div>

    <!-- Filter chips -->
    <div class="d-flex flex-wrap gap-2 mb-3">
      <span v-for="f in filters" :key="f.k"
            class="mb-chip" :class="{active: filter === f.k}"
            @click="filter = f.k">
        {{ f.label }}
        <span v-if="counts[f.k]" class="mb-chip-count">{{ counts[f.k] }}</span>
      </span>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-warning"></div>
    </div>

    <div v-else-if="!filteredBills.length" class="text-center py-5">
      <i class="mdi mdi-file-document-outline" style="font-size:48px;color:#BFA164;opacity:.5;"></i>
      <p class="mt-2 mb-1" :style="{color:darkMode?'#F5F0E8':'#1A1A1A'}">No bills to show</p>
      <p class="mb-3" style="font-size:13px;color:#6B6356;">Send your first bill — buyers can pay securely with escrow protection.</p>
      <button class="btn btn-warning fw-bold" @click="$router.push('/sell/bills/new')">
        <i class="mdi mdi-plus me-1"></i>Create bill
      </button>
    </div>

    <div v-else>
      <div v-for="b in filteredBills" :key="b.id" class="mb-card" @click="openBill(b)">
        <div class="d-flex justify-content-between align-items-start">
          <div class="flex-fill" style="min-width:0;">
            <div style="font-weight:800;color:#BFA164;font-family:'Courier New',monospace;">{{ b.bill_number }}</div>
            <div style="font-size:14px;font-weight:600;">{{ b.buyer_name || b.buyer_phone || 'Unnamed buyer' }}</div>
            <div style="font-size:11px;color:#6B6356;">
              <i class="mdi mdi-clock-outline"></i> {{ formatDate(b.created_at) }}
              <span v-if="b.expires_at && b.status === 'sent'"> · expires {{ formatDate(b.expires_at) }}</span>
            </div>
          </div>
          <div class="text-end">
            <div style="font-weight:800;color:#BFA164;">{{ b.currency }} {{ fNum(b.total) }}</div>
            <span class="mb-status" :class="'mb-status-'+b.status">{{ b.status }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Detail modal -->
    <div v-if="detail" class="mb-modal-backdrop" @click.self="detail = null">
      <div class="mb-modal">
        <button class="mb-modal-close" @click="detail = null"><i class="mdi mdi-close"></i></button>
        <div style="font-weight:800;color:#BFA164;font-family:'Courier New',monospace;">{{ detail.bill_number }}</div>
        <div style="font-size:16px;font-weight:700;margin-bottom:4px;">{{ detail.buyer_name || detail.buyer_phone }}</div>
        <div style="font-size:12px;color:#6B6356;margin-bottom:12px;">Status: <b>{{ detail.status }}</b> · {{ formatDate(detail.created_at) }}</div>

        <div class="mb-total-line"><span>Subtotal</span><span>{{ detail.currency }} {{ fNum(detail.subtotal) }}</span></div>
        <div v-if="Number(detail.transport_fee) > 0" class="mb-total-line"><span>Transport</span><span>{{ detail.currency }} {{ fNum(detail.transport_fee) }}</span></div>
        <div v-if="Number(detail.discount) > 0" class="mb-total-line" style="color:#50C878;"><span>Discount</span><span>−{{ detail.currency }} {{ fNum(detail.discount) }}</span></div>
        <div class="mb-total-line mb-total-grand"><span>Total buyer pays</span><span>{{ detail.currency }} {{ fNum(detail.total) }}</span></div>
        <div class="mb-total-line" style="color:#6B6356;"><span>Platform fee — deducted from your payout</span><span>−{{ detail.currency }} {{ fNum(detail.platform_fee) }}</span></div>

        <div class="d-flex gap-2 flex-wrap mt-3">
          <button class="btn btn-sm btn-outline-warning" @click="copyText(publicUrl(detail.public_token))">
            <i class="mdi mdi-content-copy me-1"></i>Copy link
          </button>
          <a class="btn btn-sm btn-success" :href="whatsappHref(detail)" target="_blank"><i class="mdi mdi-whatsapp me-1"></i>WhatsApp</a>
          <a class="btn btn-sm btn-outline-info" :href="smsHref(detail)"><i class="mdi mdi-message-text me-1"></i>SMS</a>
          <button v-if="['sent','countered','accepted'].includes(detail.status)" class="btn btn-sm btn-outline-danger" @click="cancelBill(detail)">
            <i class="mdi mdi-close-circle-outline me-1"></i>Cancel bill
          </button>
        </div>
      </div>
    </div>

  </div>
</div>
</template>

<script>
/* eslint-disable */
import { executeJson, flocker } from '@/api';

export default {
  name: 'MyBills',
  data(){
    return {
      darkMode: (document.documentElement.getAttribute('data-theme') === 'dark'),
      loading: true,
      bills: [],
      detail: null,
      filter: 'all',
      filters: [
        { k: 'all', label: 'All' },
        { k: 'sent', label: 'Sent' },
        { k: 'accepted', label: 'Accepted' },
        { k: 'paid', label: 'Paid' },
        { k: 'countered', label: 'Countered' },
        { k: 'rejected', label: 'Rejected' },
        { k: 'expired', label: 'Expired' },
        { k: 'draft', label: 'Drafts' },
        { k: 'cancelled', label: 'Cancelled' }
      ]
    };
  },
  computed: {
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    counts(){
      var c = { all: this.bills.length };
      this.bills.forEach(b => { c[b.status] = (c[b.status] || 0) + 1; });
      return c;
    },
    filteredBills(){
      if (this.filter === 'all') return this.bills;
      return this.bills.filter(b => b.status === this.filter);
    }
  },
  mounted(){ this.load(); },
  methods: {
    async load(){
      this.loading = true;
      try {
        var r = await executeJson({ function: 'getMyBills', sellerId: sessionStorage.getItem('userId') || '' }, flocker);
        if (r.data && r.data.success) this.bills = r.data.data.bills || [];
      } catch(e){}
      this.loading = false;
    },
    openBill(b){ this.detail = b; },
    async cancelBill(b){
      if (!confirm('Cancel this bill? The buyer won\'t be able to pay it.')) return;
      try {
        var r = await executeJson({ function: 'cancelBill', billId: b.id, sellerId: sessionStorage.getItem('userId') }, flocker);
        if (r.data && r.data.success) { this.detail = null; this.load(); }
        else alert((r.data && r.data.message) || 'Failed to cancel');
      } catch(e) { alert('Network error'); }
    },
    fNum(n){ return Number(n || 0).toLocaleString('en-KE', { maximumFractionDigits: 2 }); },
    formatDate(d){ if (!d) return ''; return new Date(String(d).replace(' ','T')).toLocaleDateString('en-KE', { day:'numeric', month:'short', year:'numeric' }); },
    publicUrl(token){ return window.location.origin + '/bills/' + token; },
    whatsappHref(b){
      var msg = 'Hi, here\'s your Flockr bill ' + b.bill_number + ': ' + b.currency + ' ' + this.fNum(b.total) + '. Pay via M-Pesa: ' + this.publicUrl(b.public_token);
      var phone = String(b.buyer_phone || '').replace(/\D/g, '');
      return 'https://wa.me/' + phone + '?text=' + encodeURIComponent(msg);
    },
    smsHref(b){
      var phone = String(b.buyer_phone || '').replace(/\D/g, '');
      if (phone.substr(0,3) === '254') phone = '0' + phone.substr(3);
      var msg = 'Flockr bill ' + b.bill_number + ': ' + b.currency + ' ' + this.fNum(b.total) + ' — ' + this.publicUrl(b.public_token);
      return 'sms:' + phone + '?body=' + encodeURIComponent(msg);
    },
    copyText(text){
      if (!text) return;
      try { navigator.clipboard.writeText(text); } catch(e){}
    }
  }
};
</script>

<style scoped>
.mb-chip { background:rgba(191,161,100,.08); color:#A09888; padding:6px 12px; border-radius:50px; font-size:12px; font-weight:600; cursor:pointer; transition:all .15s; display:inline-flex; align-items:center; gap:4px; }
.mb-chip:hover { background:rgba(191,161,100,.16); color:#BFA164; }
.mb-chip.active { background:#BFA164; color:#FFFFFF; }
.mb-chip-count { background:rgba(255,255,255,.25); border-radius:8px; padding:1px 6px; font-size:10px; }
.mb-card { background:#FFFFFF; border:1px solid #E8E2DA; border-radius:10px; padding:14px; margin-bottom:10px; cursor:pointer; transition:all .15s; }
.mb-card:hover { border-color:#BFA164; transform:translateY(-1px); }
html[data-theme="dark"] .mb-card { background:#141414; border-color:#1E1E1E; color:#F5F0E8; }
.mb-status { display:inline-block; padding:2px 10px; border-radius:10px; font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.5px; }
.mb-status-draft { background:rgba(107,99,86,.15); color:#6B6356; }
.mb-status-sent { background:rgba(255,171,0,.15); color:#FFAB00; }
.mb-status-accepted { background:rgba(79,195,247,.15); color:#4FC3F7; }
.mb-status-paid { background:rgba(80,200,120,.15); color:#50C878; }
.mb-status-countered { background:rgba(206,147,216,.15); color:#CE93D8; }
.mb-status-rejected { background:rgba(255,107,107,.15); color:#FF6B6B; }
.mb-status-cancelled { background:rgba(107,99,86,.15); color:#6B6356; }
.mb-status-expired { background:rgba(107,99,86,.15); color:#6B6356; }
.mb-modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.6); display:flex; align-items:center; justify-content:center; z-index:9000; padding:16px; }
.mb-modal { background:#FFFFFF; border-radius:14px; padding:24px; width:100%; max-width:480px; position:relative; color:#1A1A1A; }
.mb-modal-close { position:absolute; top:8px; right:10px; background:transparent; border:none; font-size:22px; color:#6B6356; cursor:pointer; }
.mb-total-line { display:flex; justify-content:space-between; padding:4px 0; font-size:13px; }
.mb-total-grand { border-top:1px solid #BFA164; font-weight:800; font-size:16px; padding-top:10px; margin-top:6px; color:#BFA164; }
</style>
